import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import BigPicMain, { BigPicHeader } from './BigPicMain';
import './SimpleBigPic.scss';
import intl from 'react-intl-universal';
import {
  debounce,
  formatSize,
  getWatermarks,
  isWeb,
  getPageSameType,
  navigatorTo
} from '../../utils/util';
import imageUtil, { downloadImage } from '../../utils/image';
import stat from '../../utils/stat';
import ReactSVG from 'react-svg';
import RootStore from '../../store/RootStore';
import { inject, observer } from 'mobx-react';
import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll
} from 'body-scroll-lock';
import usePicPay from './usePicPay';
import { customizeCheckCanDownload } from 'utils/customize';

interface ISimpleBigPic {
  pageType: string;
  index: number;
  list: any[];
  setIndex: (index: number) => void;
  style?: CSSProperties;
  loadBigPic?: () => void;
  toNext?: () => void;
  disablePinchZoom?: boolean;
  loadFinish?: (index: number, size?: number) => void;
  rootStore?: RootStore;
  showDetail?: boolean;
  bigPicInfoRender?: ReactNode;
  bigPicTopInfoRender?: ReactNode;
}

let containerEl = {} as any;
const SimpleBigPic = function (props: ISimpleBigPic) {
  let { pageType } = props;

  if (!containerEl[pageType]) {
    containerEl[pageType] = document.createElement('div');
  }

  useEffect(() => {
    if (!containerEl[pageType].parentNode)
      document.body.appendChild(containerEl[pageType]);
    return () => {
      if (containerEl[pageType].parentNode)
        document.body.removeChild(containerEl[pageType]);
    };
  }, [props.pageType]);
  const { index, list, setIndex } = props;
  // todo: 不知道为什么切换的时候会报警，虽然目前没有发现什么实质的问题
  // 先放这里了，等后面有空看看是什么导致的问题
  // 奇怪，这里的警告已经不见了

  const nextClick = () => {
    if (index + 1 >= list.length) {
      props.rootStore!.uiStore.showToast({
        content: intl.get('This is the last one photo'),
        time: 1000
      });
      return;
    }
    setIndex(index + 1);
  };

  const { galleryStore } = props.rootStore!;
  const [downloading, setDownloading] = useState(false);
  const downloadToast = (path: string, name: string) => {
    if (!downloading) {
      setDownloading(true);
      props.rootStore!.uiStore.showToast({
        content: intl.get('Downloading'),
        time: 0
      });
      downloadImage(path, name).then(() => {
        props.rootStore!.uiStore.hideToast();
        setDownloading(false);
      });
    }
  };

  const longPress = () => {
    const image = list[index];
    stat.event('image_download', image.id);
  };

  const keyup = (e: { keyCode: number }) => {
    let targetIndex = index;
    if (e.keyCode == 37) {
      targetIndex = Math.max(0, targetIndex - 1);
    }
    if (e.keyCode == 39) {
      if (targetIndex === list.length - 1) {
        props.rootStore!.uiStore.showToast({
          content: intl.get('This is the last one photo'),
          time: 1000
        });
      }
      targetIndex = Math.min(list.length - 1, targetIndex + 1);
    }
    var target = list[targetIndex];
    if (target && target.id) {
      props.setIndex(targetIndex);
    }
  };

  // 开启时注册keyup事件，销毁时取消掉
  useEffect(() => {
    index !== -1 && isWeb() && window.addEventListener('keyup', keyup);

    if (index !== -1) {
      disableBodyScroll(window.document.body);
    }
    return () => {
      isWeb() && window.removeEventListener('keyup', keyup);

      if (index !== -1) {
        clearAllBodyScrollLocks();
      }
    };
  }, [index]);

  const { wxuserStore } = props.rootStore!;

  const showDetailButton = (item: any) => {
    return !!props.showDetail && !!item.link && !!item.link.type;
  };

  const showDownloadButton = (item: any) => {
    return (
      !showDetailButton(item) &&
      !wxuserStore.user.is_mock &&
      !window.isweixin &&
      !window.isweixinweb &&
      window.screen.availWidth > 800
    );
  };

  const { currentPayWatermark, currentPayId, currentIndexIsPayed } = usePicPay({
    rootStore: props.rootStore,
    list,
    index,
  });

  const canUseInherit = () => {
    if (currentPayWatermark) {
      return Boolean(currentIndexIsPayed);
    }

    return true;
  };

  const checkUsePay = (callback: any) => {
    if (!canUseInherit()) {
      // 先检查是否登录
      if (wxuserStore.user.is_mock) {
        return wxuserStore.auth();
      }

      // 付费请求
      const image = list[index];
      if (image && image.id) {
        const notPayed = wxuserStore.getNotPayed([Number(image.id)]);
        // let notPayed = [id];
        if (notPayed && notPayed.length) {
          // 弹出付费
          const currentGalleryData = window.dataDetail ? window.dataDetail : window.gallery;
          props.rootStore!.uiStore.showToast({ content: intl.get("ReadyToPay") });
          wxuserStore.payToImage(currentGalleryData.id, Number(currentPayId), window.pageDetail.link_id, notPayed).then((res: any) => {
            // 重新检查一下是否含有当前照片
            const realPayed = wxuserStore.getNotPayed([Number(image.id)]);
            if (realPayed && realPayed.length == 0) {
              callback && callback();
            }
          });
        }
      }
    }
    else {
      callback && callback();
    }
  };

  const checkBigPic = () => {
    if (!canUseInherit()) {
      return intl.get('PayToRemove');
    }
    else {
      return (list[index].loadStatus === "loading"
        ? intl.get("Loading")
        : intl.get("Full image")) + (list[index].size ? formatSize(list[index].size) : '');
    }
  }

  return createPortal(
    index !== -1 ? (
      <div
        className={`simple-big-pic simple-big-pic-${String(pageType || '').toLowerCase()}`}
        onClick={() => setIndex(-1)}
        style={props.style}
      >
        <BigPicMain
          currentImageIndex={index}
          list={list}
          setCurrentImageIndex={setIndex}
          onLongPress={longPress}
          hideBigPic={() => setIndex(-1)}
          loadFinish={props.loadFinish}
          disablePinchZoom={props.disablePinchZoom}
          currentPayWatermark={currentPayWatermark}
          currentIndexIsPayed={currentIndexIsPayed}
          currentPayGoodId={currentPayId}
        />
        <div className="bigpic-wrapper" onClick={e => e.stopPropagation()}>
          <BigPicHeader
            detail={list[index]}
            currentImageIndex={index}
            count={list.length}
          ></BigPicHeader>
          <div className="bigpic-operation">
            <img
              src={require('../../assets/x-circle.png')}
              alt=""
              className="bigpic-close position-simple-big-pic"
              onClick={() => setIndex(-1)}
            />
          </div>
          {/*随意写一个高度试试，IOS上显示有问题，不明为什么*/}
          <div style={{ flex: 1, zIndex: -1, height: 100 }} />
          <div className="bigpic-bottom-stack">
            {props.bigPicTopInfoRender ? (
              <div className="bigpic-bottom-top-row">
                {props.bigPicTopInfoRender}
              </div>
            ) : null}
            <div className="bigpic-bottom">
              {/* 从我的喜欢进来的目前没有返回size，所以这里做判断隐藏*/}
              <div className="bigpic-bottom-info-wrapper">
                {props.loadBigPic ? (
                  list[index].loadStatus === "loaded" ? (
                    <div className="big-pic-info">
                      <span>{intl.get("Loaded") + " "}</span>
                    </div>
                  ) : (
                    <span onClick={async () => {
                      const [error, canDownload] = await customizeCheckCanDownload(list[index].id, props.rootStore?.uiStore);
                      if (!error && canDownload) {
                        checkUsePay(() => {
                          props.loadBigPic && props.loadBigPic();
                        })
                      }
                    }} className="big-pic-info">
                      {checkBigPic()}
                    </span>
                  )
                ) : null}

                {showDetailButton(list[index]) ? (
                  <span
                    className="big-pic-info"
                    onClick={() => {
                      if (list[index].link) {
                        navigatorTo(list[index].link);
                      }
                    }}
                  >
                    {intl.get('SeeMore')}
                  </span>
                ) : null}

                {showDownloadButton(list[index]) ? (
                  <span
                    className="big-pic-info"
                    onClick={async () => {
                      if (wxuserStore.user.is_mock) {
                        return wxuserStore.auth();
                      }

                      const [error, canDownload] = await customizeCheckCanDownload(list[index].id, props.rootStore?.uiStore);
                      if (!error && canDownload) {
                        checkUsePay(() => {
                          downloadToast(
                            imageUtil.style(
                              list[index].hash,
                              'original',
                              {
                                watermark: galleryStore.getWatermarksByKey(
                                  String(list[index].galleryid),
                                  typeof list[index].show_mark != 'undefined'
                                    ? list[index].show_mark
                                    : 1
                                )
                              },
                              list[index].id
                            ),
                            list[index].origin || `Bigpic${list[index].id}.jpg`
                          )
                        });
                      }
                    }}
                  >
                    {intl.get('Download')}
                  </span>
                ) : null}

                {props.bigPicInfoRender}
              </div>
              <div style={{ flex: 1 }} />

              <div
                className="icon"
                style={{ marginRight: 15 }}
                onClick={nextClick}
              >
                <ReactSVG
                  src={require('../../svg/right-arrow.svg')}
                  className={'icon-normal icon-right-arrow2'}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : null,
    containerEl[pageType]
  );
};

export default inject('rootStore')(observer(SimpleBigPic));
